import React, { useEffect, useState } from 'react';

import Typography from 'components/uiLibrary/Typography';
import IconCard from 'components/uiLibrary/IconCard';
import useTracking from 'components/Globals/Analytics';
import { PrimaryButton } from 'components/uiLibrary/LinkButton';
import { enqueueSnackbar, SNACKBAR_VARIANTS } from 'components/uiLibrary/Snackbar';
import { ACTION_CALLBACK_STATUS, ACTION_CALLBACK_STEPS, SUB_COMPONENTS } from 'components/Globals/Analytics/constants';

import { errorMessageConverter } from 'containers/Accounts/utils';
import { useResendOtpMutation } from 'containers/Accounts/queries';

import { getUserByEmail } from 'utils/API';
import { useDialogs } from 'utils/hooks/useDialogs';
import { useTranslation } from 'src/i18n';
import { TP } from 'constants/index';

import classes from './DeviceManagement.module.scss';

export const useLoginLink = ({
  track,
  trackingData,
  isReturningUser,
  isLoginDialog,
  setCurrentEmail,
  handleFormType,
  allSteps,
  setLoginViaOtp,
  accountSharing,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const { mutate: sendOtpMutation } = useResendOtpMutation({
    onError: error => {
      enqueueSnackbar(errorMessageConverter(error), { variant: SNACKBAR_VARIANTS.ERROR });
      track.actionCallback({
        ...trackingData,
        meta: {
          step: ACTION_CALLBACK_STEPS.SEND_PASSCODE_TO_EMAIL,
          actionStatus: ACTION_CALLBACK_STATUS.FAILURE,
          isReturningUser,
          errorMessage: errorMessageConverter(error),
        },
      });
    },
    onSuccess: () => {
      track.actionCallback({
        ...trackingData,
        meta: {
          step: ACTION_CALLBACK_STEPS.SEND_PASSCODE_TO_EMAIL,
          actionStatus: ACTION_CALLBACK_STATUS.SUCCESS,
          isReturningUser,
        },
      });
      handleFormType(allSteps.VALIDATE);
    },
  });

  const handleLoginLink = async userEmail => {
    track.click({
      ...trackingData,
      subComponent: SUB_COMPONENTS.SECURITY_CODE_LINK,
      meta: { isReturningUser },
    });

    setIsLoading(true);
    try {
      const response = await getUserByEmail(accountSharing?.email || userEmail);

      if (!response?.data?.email) {
        track.actionCallback({
          ...trackingData,
          meta: {
            step: ACTION_CALLBACK_STEPS.SEND_PASSCODE_TO_EMAIL,
            actionStatus: ACTION_CALLBACK_STATUS.FAILURE,
            payload: { email: accountSharing?.email || userEmail },
            errorMessage: 'Invalid Email',
          },
        });
      } else {
        sendOtpMutation({
          email: accountSharing?.email || userEmail,
          otpType: 'EMAIL_LOGIN',
          popupSource: isLoginDialog?.source,
        });
        setCurrentEmail(accountSharing?.email || userEmail);
        setLoginViaOtp(true);
      }
    } catch (error) {
      enqueueSnackbar(errorMessageConverter(error), { variant: SNACKBAR_VARIANTS.ERROR });
    } finally {
      setIsLoading(false);
    }
  };

  return { handleLoginLink, isLoading };
};

const DeviceManagement = ({
  trackingData,
  accountSharing,
  setCurrentEmail,
  handleFormType,
  allSteps = {},
  isReturningUser = false,
  setLoginViaOtp,
}) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const track = useTracking();
  const { isLoginDialog } = useDialogs();

  const { handleLoginLink, isLoading } = useLoginLink({
    track,
    trackingData,
    isReturningUser,
    isLoginDialog,
    setCurrentEmail,
    handleFormType,
    allSteps,
    setLoginViaOtp,
    accountSharing,
  });

  useEffect(() => {
    track.actionCallback({
      ...trackingData,
      meta: {
        step: ACTION_CALLBACK_STEPS.VIEW_DEVICE_MANAGEMENT,
        actionStatus: ACTION_CALLBACK_STATUS.SUCCESS,
      },
    });
  }, []);

  return (
    <div className={classes.deviceManagement}>
      <div className={classes.deviceManagement__header}>
        <Typography size="18" weight="medium" className={classes.deviceManagement__header_title}>
          {t(`${TP}.DEVICE_MANAGEMENT_TITLE`)}
        </Typography>
        <Typography size="14" className={classes.deviceManagement__header_subtitle}>
          {t(`${TP}.DEVICE_MANAGEMENT_SUB_TITLE`)}
        </Typography>
      </div>
      <div className={classes.deviceManagement__content}>
        <div className={classes.iconCardContainer}>
          <IconCard
            title={accountSharing?.os}
            subTitle={`${accountSharing?.browser} | ${accountSharing.lastLoginTime}`}
            description={`${accountSharing?.city}, ${accountSharing?.country}`}
            showCircle={false}
            style={classes.iconCardWrapper}
            onClick={() => {}}
            isHover
            trackingData={trackingData}
            showChevron={false}
          />
        </div>
      </div>
      <div className={classes.deviceManagement__footer}>
        <PrimaryButton shape="rectangle" onClick={handleLoginLink} loading={isLoading}>
          {t(`${TP}.KEEP_ME_SIGN_IN`)}
        </PrimaryButton>
      </div>
    </div>
  );
};

export default DeviceManagement;
